import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CircularProgress, IconButton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getErrorSelector, getLLMPromptsArraySelector, isLoadingGetLLMPromptsSelector } from '@redux/llmPrompts/llmPromptsSelectors';
import { deleteLLMPromptThunk, getLLMPromptsThunk, updateLLMPromptThunk } from '@redux/llmPrompts/llmPromptsThunks';
import { LLMPrompt } from '@redux/llmPrompts/llmPromptsTypes';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { TrashIcon } from 'components/Icons/TrashIcon';
import NavTopBarContentWrapper from 'components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { Text } from 'components/Typography/Text';
import { truncateInstruction } from 'utils/util';
import LLMPromptFormDialog from './components/LLMPromptFormDialog';
import style from './LLMPromptsManager.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux;

const LLMPromptsManager: React.FC<Props> = ({ llmPrompts, isLoadingGetPrompts, errors, getLLMPrompts, deleteLLMPrompt, updateLLMPrompt }) => {
  const flags = useFlags();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<LLMPrompt | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; promptId?: string; promptName?: string }>({ open: false });

  // Check LaunchDarkly flag
  const llmPromptsEditingEnabled = flags.llmPromptsEditing ?? false;

  useEffect(() => {
    if (llmPromptsEditingEnabled) {
      getLLMPrompts();
    }
  }, [llmPromptsEditingEnabled, getLLMPrompts]);

  const handleOpenDialog = (prompt?: LLMPrompt) => {
    setSelectedPrompt(prompt || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPrompt(null);
  };

  const handleDeleteClick = (promptId: string, promptName: string) => {
    setDeleteConfirmation({ open: true, promptId, promptName });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmation.promptId) {
      await deleteLLMPrompt(deleteConfirmation.promptId);
      getLLMPrompts();
    }
    setDeleteConfirmation({ open: false });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ open: false });
  };

  const handleToggleActive = async (prompt: LLMPrompt) => {
    await updateLLMPrompt({
      ...prompt,
      isActive: !prompt.isActive
    });
    getLLMPrompts();
  };

  const handleSaveSuccess = () => {
    handleCloseDialog();
    getLLMPrompts();
  };

  const getPromptsError = errors['llm-prompts-redux/get-llm-prompts'];

  // Show not authorized message if flag is disabled
  if (!llmPromptsEditingEnabled) {
    return (
      <NavTopBarContentWrapper centerAlignContent headerText="LLM Prompts Management">
        <div className={style.mainContainer}>
          <div className={style.emptyState}>
            <Text variant="md">Not authorized</Text>
          </div>
        </div>
      </NavTopBarContentWrapper>
    );
  }

  return (
    <NavTopBarContentWrapper centerAlignContent headerText="LLM Prompts Management">
      <div className={style.mainContainer}>
        <div className={style.header}>
          <Text variant="xl" className={style.title}>
            LLM Prompts
          </Text>
          <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
            Add New Prompt
          </Button>
        </div>

        {isLoadingGetPrompts ? (
          <div className={style.loadingContainer}>
            <CircularProgress />
          </div>
        ) : getPromptsError ? (
          <div className={style.errorContainer}>
            <Text variant="md">Error loading prompts: {getPromptsError?.message}</Text>
          </div>
        ) : (
          <TableContainer className={style.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tool Name</TableCell>
                  <TableCell>Tool Rule ID</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Instruction</TableCell>
                  <TableCell>Section Content</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {llmPrompts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className={style.emptyState}>
                        <Text variant="md">No LLM prompts found. Click "Add New Prompt" to create one.</Text>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  llmPrompts.map((prompt) => (
                    <TableRow key={prompt.id} hover>
                      <TableCell>{prompt.toolName}</TableCell>
                      <TableCell>{prompt.toolRuleId || '-'}</TableCell>
                      <TableCell>{prompt.version}</TableCell>
                      <TableCell>{truncateInstruction(prompt.instruction)}</TableCell>
                      <TableCell>{truncateInstruction(prompt.sectionContent)}</TableCell>
                      <TableCell>
                        <Switch checked={prompt.isActive} onChange={() => handleToggleActive(prompt)} color="primary" />
                      </TableCell>
                      <TableCell>{new Date(prompt.updatedAt).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <div className={style.actionButtons}>
                          <IconButton onClick={() => handleOpenDialog(prompt)} color="primary" size="small">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteClick(prompt.id, prompt.toolName)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <LLMPromptFormDialog open={openDialog} onClose={handleCloseDialog} onSuccess={handleSaveSuccess} prompt={selectedPrompt} existingPrompts={llmPrompts} />

        <ConfirmationDialogV2
          open={deleteConfirmation.open}
          icon={TrashIcon}
          title="Delete LLM Prompt"
          description={`Are you sure you want to delete the LLM prompt "${deleteConfirmation.promptName}"? This action cannot be undone.`}
          proceedEvent={handleDeleteConfirm}
          proceedEventText="Delete"
          closeEvent={handleDeleteCancel}
          closeEventText="Cancel"
        />
      </div>
    </NavTopBarContentWrapper>
  );
};

export { LLMPromptsManager };

const connectRedux = connect(
  (state: ReduxState) => ({
    llmPrompts: getLLMPromptsArraySelector(state),
    isLoadingGetPrompts: isLoadingGetLLMPromptsSelector(state),
    errors: getErrorSelector(state)
  }),
  {
    getLLMPrompts: getLLMPromptsThunk,
    deleteLLMPrompt: deleteLLMPromptThunk,
    updateLLMPrompt: updateLLMPromptThunk
  }
);

export default compose(connectRedux)(LLMPromptsManager);
